/**
 * Grid Layout Module
 * Responsive CSS Grid implementation for Instagram posts
 */

/**
 * Grid layout renderer
 */
export class GridLayout {
  /**
   * Creates a grid layout instance
   * @param {Object} config - Layout configuration
   */
  constructor(config = {}) {
    this.columns = config.columns || 3;
    this.gap = config.gap || 16;
    this.aspectRatio = config.aspectRatio || '1/1';
    this.openInLightbox = config.openInLightbox !== false;
    this.onItemClick = config.onItemClick || null;
  }

  /**
   * Renders the grid layout
   * @param {Array} posts - Instagram posts data
   * @param {HTMLElement} container - Container element
   * @returns {HTMLElement} Rendered grid element
   */
  render(posts, container) {
    const grid = document.createElement('div');
    grid.className = 'ig-widget-grid';
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${this.columns}, 1fr);
      gap: ${this.gap}px;
      width: 100%;
    `;

    // Add responsive breakpoints
    this.addResponsiveStyles(container);

    posts.forEach((post, index) => {
      const item = this.createGridItem(post, index);
      grid.appendChild(item);
    });

    return grid;
  }

  /**
   * Creates a single grid item
   * @param {Object} post - Instagram post data
   * @param {number} index - Post index
   * @returns {HTMLElement} Grid item element
   */
  createGridItem(post, index) {
    const item = document.createElement(this.openInLightbox ? 'div' : 'a');
    item.className = 'ig-widget-grid-item';

    if (this.openInLightbox) {
      item.style.cursor = 'pointer';
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', `View ${post.caption || 'Instagram post'}`);

      // Click handler for lightbox
      item.addEventListener('click', () => {
        if (this.onItemClick) {
          this.onItemClick(index);
        }
      });

      // Keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.onItemClick) {
            this.onItemClick(index);
          }
        }
      });
    } else {
      item.href = post.permalink;
      item.target = '_blank';
      item.rel = 'noopener noreferrer';
      item.setAttribute('aria-label', post.caption || 'Instagram post');
    }

    // Determine media URL based on type
    const mediaUrl = post.media_type === 'VIDEO'
      ? (post.thumbnail_url || post.media_url)
      : post.media_url;

    item.innerHTML = `
      <div class="ig-widget-grid-item-inner" style="
        position: relative;
        aspect-ratio: ${this.aspectRatio};
        overflow: hidden;
        background: var(--ig-widget-bg-secondary, #f5f5f5);
      ">
        <img
          src="${mediaUrl}"
          alt="${this.escapeHtml(post.caption || 'Instagram post')}"
          loading="lazy"
          style="
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          "
        />
        <div class="ig-widget-grid-overlay" style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          padding: 16px;
        ">
          <div style="
            color: white;
            text-align: center;
            font-size: 14px;
            line-height: 1.4;
            max-height: 100%;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
          ">
            ${this.escapeHtml(post.caption || 'View on Instagram')}
          </div>
        </div>
        ${post.media_type === 'VIDEO' ? '<div class="ig-widget-video-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5);">▶</div>' : ''}
        ${post.media_type === 'CAROUSEL_ALBUM' ? '<div class="ig-widget-carousel-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5);">⋮⋯</div>' : ''}
      </div>
    `;

    // Add hover effect
    item.addEventListener('mouseenter', () => {
      const overlay = item.querySelector('.ig-widget-grid-overlay');
      const img = item.querySelector('img');
      if (overlay) overlay.style.opacity = '1';
      if (img) img.style.transform = 'scale(1.05)';
    });

    item.addEventListener('mouseleave', () => {
      const overlay = item.querySelector('.ig-widget-grid-overlay');
      const img = item.querySelector('img');
      if (overlay) overlay.style.opacity = '0';
      if (img) img.style.transform = 'scale(1)';
    });

    return item;
  }

  /**
   * Adds responsive styles to container
   * @param {HTMLElement} container - Container element
   */
  addResponsiveStyles(container) {
    const styleId = 'ig-widget-grid-responsive';

    // Remove existing style if present
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (max-width: 768px) {
        .ig-widget-grid {
          grid-template-columns: repeat(${Math.max(2, Math.floor(this.columns / 2))}, 1fr) !important;
        }
      }
      @media (max-width: 480px) {
        .ig-widget-grid {
          grid-template-columns: repeat(${Math.max(1, Math.floor(this.columns / 3))}, 1fr) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Updates layout configuration
   * @param {Object} config - New configuration
   */
  update(config) {
    if (config.columns !== undefined) this.columns = config.columns;
    if (config.gap !== undefined) this.gap = config.gap;
    if (config.aspectRatio !== undefined) this.aspectRatio = config.aspectRatio;
  }
}

export default GridLayout;
